import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Skeleton, Typography, useTheme, Button } from '@mui/material';


import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { Table, usePagination,} from '@frontend/features/common/components/Table';
import { ListLayout } from '@frontend/features/common/components/ListLayout';

import { useGetWpActiveConversations } from '@frontend/features/whatsapp/hooks/useGetWpActiveConversations';
import { WHATSAPP_SESSION_DURATION_IN_MILLISECONDS } from '@bandido/shared/constants';
import { useWhatsappClient } from '@frontend/features/whatsapp/components/WhatsappClientContext';
import { routes } from '../routes';
import { IconAvatar } from '@frontend/features/common/components/IconAvatar';
import { Person } from '@mui/icons-material';
import { Link } from '@frontend/features/common/components/Link';

export const SessionCountdown = ({ lastClientMessage }: { lastClientMessage: string }) => {
  const [remaining, setRemaining] = useState(0);
  const [isSessionExpired, setIsSessionExpired] = useState(false);

  useEffect(() => {
    if (!lastClientMessage) return;

    const createdTime = new Date(lastClientMessage).getTime();
    const expiresAt = createdTime + WHATSAPP_SESSION_DURATION_IN_MILLISECONDS;

    let interval: NodeJS.Timeout;

    const updateRemaining = () => {
      const now = Date.now();
      const timeLeft = Math.max(0, expiresAt - now);
      setRemaining(timeLeft);

      if (timeLeft <= 0) {
        setIsSessionExpired(true);
        clearInterval(interval);
      } else {
        if (isSessionExpired === true) {
          setIsSessionExpired(false)
        }
      }
    };

    updateRemaining();
    interval = setInterval(updateRemaining, 1000);

    return () => clearInterval(interval);
  }, [lastClientMessage, isSessionExpired]);

  const formatTime = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
      <Typography variant="caption">
        {(isSessionExpired || lastClientMessage === null) ? 'Session expired' : `Time left: ${formatTime(remaining)}`}
      </Typography>
    </Box>
  );
};

export const WhatsappList = () => {
  const { t } = useTranslation();
  const pagination = usePagination();
  const theme = useTheme();
  
  const  { openWhatsapp} = useWhatsappClient();
  const [search, setSearch] = useState('');

  const { data, isLoading } = useGetWpActiveConversations();

  const handleOnSearch = (value: string) => {
    setSearch(value);
  };
  

  return (
    <ListLayout title="Whatsapp convesations" subtitle={t('lista de conversaciones activas de whatsapp')}>
      <Box display="flex" alignItems="center" gap={theme.spacing(3)} marginBottom={theme.spacing(3)}>
        <Typography
          variant="subtitle1"
          align="left"
          color={theme.palette.text.primary}
          sx={{
            flexGrow: 1,
          }}
        >
          
          {data?.count || 0} {t('whatsapp.results')}
        </Typography>

        <SearchInput placeholder="Buscar" onSearch={handleOnSearch} />
      </Box>

      {data?.count !== undefined && data.count > 0 && (
        <Table
          columns={[
            {
              id: 'clientName',
              label: t('whatsapp.table.clientName'),
              align: 'left',
            },
            {
              id: 'conversation',
              label: t('whatsapp.table.conversation'),
              align: 'left',
            },
            {
              id: 'agency',
              label: t('whatsapp.table.agency'),
              align: 'left',
            },
            { id: 'msgType', label: t('whatsapp.table.msgType'), align: 'left' },
            {
              id: 'expiration',
              label: t('whatsapp.table.expiration'),
              align: 'left',
            },
          ]}
          data={{
            key: 'fromPhone',
            items: data.items.map((item) => ({
              ...item,
              clientName:(
                <Box display="flex" alignItems="center" gap={1}>
                <IconAvatar>
                  <Person fontSize="small" htmlColor={theme.palette.common.white} />
                </IconAvatar>
                <Link underline="hover" to={routes.dashboard.clientView.replace(':id', String(item.clientId))}>
                  {item.clientName} {item.clientLastName}
                </Link>
              </Box>
              ),
              conversation: (
                <Box>
                  <Button
                    variant="text"
                    onClick={() => openWhatsapp(item.clientId)}
                    sx={{
                      textTransform: 'none',
                      color: theme.palette.primary.main,
                      '&:hover': {
                        textDecoration: 'underline',
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    {item.fromPhone} ↔ {item.toPhone}
                  </Button>
                </Box>
              ),
              agency: (
                <Typography variant="body2">
                  {item.agencyName || `ID: ${item.agencyId}`}
                </Typography>
              ),
              msgType: item.lastMessageType,
              expiration: <SessionCountdown lastClientMessage={item.lastMessageAt} />,
            })),
            totalCount: data.count
          }}
          pagination={pagination}
          stickyHeader
        />
      )}
      {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
    </ListLayout>
  );
};
